import { useState, useCallback } from 'react'
import { useTeams } from '@contexts/TeamsContext'
import { getToken } from '@config/auth.config'

interface TeamsAuthResult {
  token: string | null
  error: string | null
  isLoading: boolean
  getTeamsToken: () => Promise<string | null>
}

/**
 * Hook pour gérer l'authentification dans Teams
 * Utilise MSAL avec popup car Teams SSO nécessite une configuration Azure AD spécifique
 */
export const useTeamsAuth = (): TeamsAuthResult => {
  const { isInTeams } = useTeams()
  const [token, setToken] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const getTeamsToken = useCallback(async (): Promise<string | null> => {
    if (!isInTeams) {
      setError('Not in Teams context')
      return null
    }

    setIsLoading(true)
    setError(null)

    try {
      // Utiliser MSAL directement (avec popup dans l'iframe Teams)
      // Le Teams SSO natif nécessite une configuration Azure AD "Expose an API"
      // qui peut ne pas être disponible dans tous les environnements
      const msalToken = await getToken(null)
      
      if (msalToken) {
        setToken(msalToken)
        console.log('Teams authentication successful via MSAL')
        return msalToken
      }
      
      throw new Error('Failed to get token via MSAL')
    } catch (err) {
      console.error('Teams authentication failed:', err)
      const errorMessage = err instanceof Error ? err.message : 'Authentication failed'
      setError(errorMessage)
      return null
    } finally {
      setIsLoading(false)
    }
  }, [isInTeams])

  return {
    token,
    error,
    isLoading,
    getTeamsToken,
  }
}

export default useTeamsAuth
